import { ActivityTariffFields, ChildAgeBands } from "./ChildAgeBands";
import { isDuplicateVehicle } from "@/lib/vehicleUniqueness";
import { useGetActivity } from "@/actions/activity.actions";
import { useListSuppliersQuery } from "@/actions/suppliers.action";
import { ImageUpload } from "@/components";
import { usePortalVisibilityConfirmation } from "@/hooks/usePortalVisibilityConfirmation";
import { ActivityType } from "@/interfaces";
import { activityCategories, activityVehicleTypes } from "@/lib/constants";
import { createInventoryDuplicateDraft } from "@/lib/inventoryDuplicate";
import { useBuildOrgRoute } from "@/lib/orgRoutes";
import type { OrganisationOutletContext } from "@/pages/OrgPage";
import { InventoryLocationFields } from "@/pages/OrgPage/components/InventoryLocationFields";
import { useAuthStore } from "@/states";
import {
  App as AntApp,
  AutoComplete,
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  UploadFile,
  UploadProps,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { MinusCircleIcon, PlusIcon, SettingsIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router";
import { OrgFormPageSkeleton, OrgPageHeader, OrgTableCard } from "../components/OrgPagePrimitives";
import VehicleRatesModal from "./VehicleRatesModal";

/**
 * Component for creating or editing an activity repository item, can be wrapped in a modal.
 */
const EditActivityRepositoryPage = ({
  isModal,
  initialName,
  initialCountry,
  initialCity,
  onSuccess,
  onCancel,
}: {
  isModal?: boolean;
  initialName?: string;
  initialCountry?: string;
  initialCity?: string;
  onSuccess?: (activity: ActivityType) => void;
  onCancel?: () => void;
} = {}) => {
  const params = useParams();
  const id = isModal ? undefined : params.id;
  const [searchParams] = useSearchParams();
  const duplicateFromId = isModal ? undefined : searchParams.get("duplicate") || undefined;
  const sourceId = id || duplicateFromId;
  const isDuplicate = !id && Boolean(duplicateFromId);
  const outletContext = useOutletContext<OrganisationOutletContext>();
  const setBreadcrumbTitle = outletContext?.setBreadcrumbTitle;
  const { isFetching, data, isError, error } = useGetActivity(sourceId);
  const formData = useMemo(() => (isDuplicate ? createInventoryDuplicateDraft(data) : data), [data, isDuplicate]);
  const { message } = AntApp.useApp();
  const { confirmHiding, invalidateVisibilityQueries } = usePortalVisibilityConfirmation();
  const user = useAuthStore((state) => state.user);
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const navigate = useNavigate();
  const buildOrgRoute = useBuildOrgRoute();
  const [country, setCountry] = useState<string>();
  const [category, setCategory] = useState<string>(data?.category || "");
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isVehicleRatesModalOpen, setIsVehicleRatesModalOpen] = useState<boolean>(false);
  const [currentVehicleIndex, setCurrentVehicleIndex] = useState<number | null>(null);

  const openVehicleRatesModal = (index: number) => {
    setCurrentVehicleIndex(index);
    setIsVehicleRatesModalOpen(true);
  };

  const closeVehicleRatesModal = () => {
    setIsVehicleRatesModalOpen(false);
    setCurrentVehicleIndex(null);
  };

  useEffect(() => {
    if (isError) {
      console.error(error);
      message.error(error?.message || "Failed to get Activities list");
    }
  }, [isError, error, message]);

  useEffect(() => {
    if (!setBreadcrumbTitle) return;
    if (id) {
      setBreadcrumbTitle(data?.name || "Edit Activity");
      return () => setBreadcrumbTitle();
    }

    setBreadcrumbTitle(isDuplicate ? "Duplicate Activity" : "New Activity");
    return () => setBreadcrumbTitle();
  }, [data?.name, id, isDuplicate, setBreadcrumbTitle]);

  useEffect(() => {
    setCountry(formData?.country || initialCountry);
    if (formData?.image) {
      setFileList([{ uid: "image", name: "image", url: formData.image }]);
    }
    setCategory(formData?.category || "");
  }, [formData, initialCountry]);

  const supplierCity = Form.useWatch("city", form);
  const { data: suppliers, isFetching: isFetchingSuppliers } = useListSuppliersQuery(country, true, supplierCity);

  const supplierOptions = useMemo(() => {
    const opts = (suppliers || []).map((s) => ({ value: s.id, label: s.name }));
    if (data?.supplier && !opts.some((o) => o.value === data.supplier?.id)) {
      opts.push({
        value: data.supplier.id,
        label: data.supplier.name,
      });
    }
    return opts;
  }, [suppliers, data?.supplier]);

  const onFinish = async (values: ActivityType) => {
    values.name = values.name.trim();
    values.description = values.description.trim();
    values.city = values.city?.trim() || "";
    values.image = (fileList && fileList.length && fileList[0].url) || undefined;
    values.vehicles =
      values.vehicles?.map((vehicle) => ({
        ...vehicle,
        vehicleType: vehicle.vehicleType?.trim() || "",
        pax: Number(vehicle.pax) || 0,
        rate: vehicle.rate || {},
      })) || [];
    if (values.category === "Transfers") {
      values.childAgeBands = [];
      values.tariff = values.tariff?.map((rate) => ({ ...rate, childAgeRates: {} }));
    }
    values.seasonal = values.seasonal?.map((seasonal) => ({
      daterange: seasonal.daterange
        ? [
            seasonal.daterange[0]
              ? seasonal.daterange[0] instanceof Date
                ? seasonal.daterange[0]
                : seasonal.daterange[0].toDate()
              : new Date(),
            seasonal.daterange[1]
              ? seasonal.daterange[1] instanceof Date
                ? seasonal.daterange[1]
                : seasonal.daterange[1].toDate()
              : new Date(),
          ]
        : [],
      reason: seasonal.reason?.trim() || "",
      tariff: seasonal.tariff.map((tariff) => ({
        ...tariff,
        ...(values.category === "Transfers" ? { childAgeRates: {} } : {}),
        costForAdult: tariff.costForAdult || 0,
        costForCWB: tariff.costForCWB || 0,
        costForInfant: tariff.costForInfant || 0,
      })),
    }));

    if (
      id &&
      data?.isActive !== false &&
      data?.shareWithAgent !== false &&
      (values.isActive === false || values.shareWithAgent === false)
    ) {
      try {
        if (!(await confirmHiding([{ type: "activity", id }]))) return;
      } catch (impactError) {
        console.error(impactError);
        message.error("Unable to check affected templates");
        return;
      }
    }

    if (id) {
      axios.put(`/api/activities/${id}`, values).then(
        (res) => {
          void invalidateVisibilityQueries();
          message.success("Activity updated successfully");
          if (isModal && onSuccess) {
            onSuccess(res.data);
          } else {
            navigate(buildOrgRoute("activities-repository"));
          }
        },
        (error) => {
          message.error(error.response?.data?.error || "Failed to update activity");
        },
      );
    } else {
      axios.post(`/api/activities`, values).then(
        (res) => {
          void invalidateVisibilityQueries();
          message.success("Activity created successfully");
          if (isModal && onSuccess) {
            onSuccess(res.data);
          } else {
            navigate(buildOrgRoute("activities-repository"));
          }
        },
        (error) => {
          message.error(error.response?.data?.error || "Failed to create activity");
        },
      );
    }
  };

  const handleChange: UploadProps["onChange"] = (info) => {
    setFileList(info.fileList);
    if (info.file.status === "uploading") {
      setUploading(true);
      return;
    }
    if (info.file.status === "error") {
      setUploading(false);
      message.error("Failed to upload image");
    }
    if (info.file.status === "done") {
      info.file.url = `${import.meta.env.VITE_ASSETS_ENDPOINT}/${info.file.response.path}`;
      info.fileList[0].url = `${import.meta.env.VITE_ASSETS_ENDPOINT}/${info.file.response.path}`;
      setUploading(false);
    }
  };

  if (isFetching) {
    return <OrgFormPageSkeleton />;
  }

  const pageTitle = id ? "Edit Activity" : isDuplicate ? "Duplicate Activity" : "Create Activity";
  const pageSubtitle = id
    ? "Update activity details, pricing, and seasonal rates"
    : isDuplicate
      ? "Review the copied details and save when you are ready to create the new activity"
      : "Add a new activity with inclusions, exclusions, and tariffs";

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        isActive: true,
        shareWithAgent: true,
        ...formData,
        name: initialName || formData?.name,
        country: initialCountry || formData?.country,
        city: initialCity || formData?.city,
        tariff: user?.tenant?.currency.map((currency) => ({
          childAgeRates: formData?.tariff?.find((t) => t.currency === currency)?.childAgeRates || {},
          costForAdult: formData?.tariff?.find((t) => t.currency === currency)?.costForAdult,
          costForCWB: formData?.tariff?.find((t) => t.currency === currency)?.costForCWB,
          costForInfant: formData?.tariff?.find((t) => t.currency === currency)?.costForInfant,
          currency: currency,
        })),
        seasonal: formData?.seasonal?.map((seasonal) => ({
          daterange: seasonal.daterange ? [dayjs(seasonal.daterange[0]), dayjs(seasonal.daterange[1])] : null,
          reason: seasonal.reason || "",
          tariff: seasonal.tariff.map((tariff) => ({
            ...tariff,
            costForAdult: tariff.costForAdult || 0,
            costForCWB: tariff.costForCWB || 0,
            costForInfant: tariff.costForInfant || 0,
          })),
        })),
        vehicles: formData?.vehicles || [],
      }}
      onFinish={onFinish}
      autoComplete="off"
      className="space-y-6"
    >
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#131B2A]">Activity overview</h2>
        </div>
        <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
          <Form.Item name="image" label="Activity Image" valuePropName="file" className="mb-0!">
            <ImageUpload
              enableCrop={true}
              uploading={uploading}
              onChange={handleChange}
              onRemove={() => setFileList([])}
              fileList={fileList}
              folder={"activities"}
            />
          </Form.Item>
          <div className="grid gap-4 md:grid-cols-2">
            <Form.Item name="name" label="Activity Name" rules={[{ required: true, message: "Enter Activity Name" }]}>
              <Input type="text" placeholder="Snorkeling, Hiking, etc." />
            </Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true, message: "Select a category" }]}>
              <Select
                showSearch
                placeholder="Select a category"
                options={activityCategories
                  .filter((item) => item.key !== "all")
                  .map((item) => {
                    return {
                      label: item.label,
                      value: item.key,
                    };
                  })}
                onSelect={(value) => setCategory(value)}
              />
            </Form.Item>
            <Form.Item
              name="duration"
              label="Duration"
              rules={[
                {
                  type: "number",
                  min: 1,
                  max: 12,
                  message: "Duration must be between 1 and 12 hours",
                },
              ]}
            >
              <InputNumber placeholder="Duration in hours" min={1} max={12} addonAfter="hours" className="!w-full" />
            </Form.Item>
            <Form.Item
              name="isActive"
              label="Status"
              valuePropName="checked"
              tooltip="Inactive activities cannot be used in new itineraries"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
            <Form.Item
              name="shareWithAgent"
              label="Visible to agencies"
              valuePropName="checked"
              tooltip="Allow agency users to select this activity in the agent portal"
            >
              <Switch checkedChildren="Visible" unCheckedChildren="Internal only" />
            </Form.Item>
          </div>
        </div>
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Enter Activity Description" }]}
        >
          <Input.TextArea placeholder="Description of the activity ..." rows={4} maxLength={3000} showCount />
        </Form.Item>
      </section>

      <Divider className="!my-2" />

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#131B2A]">Location and partnerships</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <InventoryLocationFields
            form={form}
            country={country}
            onCountryChange={setCountry}
            countryPlaceholder="Mauritius, Bali, etc."
            cityPlaceholder="Bangkok, Hanoi, etc."
            countryRequiredMessage="Enter Country of Activity"
            cityOptional
          />
          <Form.Item name={["supplier", "id"]} label="Preferred Supplier" className="md:col-span-2">
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Select a supplier"
              loading={isFetchingSuppliers}
              options={supplierOptions}
              optionFilterProp="label"
              allowClear
              notFoundContent={supplierOptions.length === 0 ? "No suppliers found for this country" : null}
            />
          </Form.Item>
        </div>
      </section>

      {["Private Tours", "Private Transfers"].includes(category) && <Divider className="!my-2" />}

      <section className="space-y-4" hidden={!["Private Tours", "Private Transfers"].includes(category)}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#131B2A]">Vehicles and rates</h2>
            <p className="text-xs text-gray-500">
              Configure vehicle types and launch the rates modal to enter base tariffs.
            </p>
          </div>
        </div>
        <Form.List name="vehicles">
          {(vehicles, { add, remove }) => (
            <div className="!space-y-4">
              {vehicles.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-[#FBFBFB] p-6 text-center text-sm text-gray-500">
                  No vehicles configured yet. Add a vehicle to start defining tariffs.
                </div>
              ) : null}

              {vehicles.map((vehicle) => (
                <Card key={vehicle.key} className="border border-gray-200 !shadow-none">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                        <Form.Item
                          name={[vehicle.name, "vehicleType"]}
                          label="Vehicle Type"
                          className="mb-0! md:w-64"
                          rules={[{ required: true, message: "Enter a vehicle type" }]}
                        >
                          <AutoComplete
                            showSearch
                            placeholder="Sedan..."
                            options={activityVehicleTypes.map((vehicle) => ({ label: vehicle, value: vehicle }))}
                            filterOption={(inputValue, option) =>
                              option!.value.toUpperCase().includes(inputValue.toUpperCase())
                            }
                          />
                        </Form.Item>
                        <Form.Item
                        name={[vehicle.name, "pax"]}
                        label="Max Pax"
                        className="mb-0! md:w-32"
                        dependencies={[["vehicles"]]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator: async () => {
                              if (isDuplicateVehicle(getFieldValue("vehicles"), vehicle.name)) {
                                throw new Error("This vehicle name and max pax already exist");
                              }
                            },
                          }),
                        ]}
                      >
                          <InputNumber min={1} max={50} />
                        </Form.Item>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          icon={<SettingsIcon size={16} />}
                          onClick={() => openVehicleRatesModal(vehicle.name)}
                          disabled={(user?.tenant?.currency || []).length === 0}
                        >
                          Manage Rates
                        </Button>
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleIcon size={16} />}
                          onClick={() => remove(vehicle.name)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              <Button
                type="dashed"
                icon={<PlusIcon size={16} />}
                onClick={() =>
                  add({
                    vehicleType: "",
                    pax: 4,
                    rate: {},
                  })
                }
                block
              >
                Add Vehicle
              </Button>
            </div>
          )}
        </Form.List>
      </section>

      <Divider className="!my-2" />

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#131B2A]">Inclusions and exclusions</h2>
        </div>
        <div className="space-y-8">
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Inclusions</h3>
            <Form.Item
              name="inclusions"
              className="mb-0!"
              rules={[
                {
                  required: false,
                  type: "array",
                },
              ]}
            >
              <Form.List name={"inclusions"}>
                {(fields, { add, remove }) => (
                  <div className="space-y-3">
                    {fields.map(({ key, name }) => (
                      <div key={key} className="flex items-start gap-2">
                        <Form.Item
                          name={[name]}
                          className="mb-0! flex-1"
                          rules={[{ required: true, message: "Enter activity inclusion" }]}
                        >
                          <Input placeholder="Candle Light Dinner, Hotel Transfers, Meals, etc..." />
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          onClick={() => {
                            remove(name);
                          }}
                          icon={<MinusCircleIcon size={16} />}
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => {
                        add();
                      }}
                      icon={<PlusIcon size={16} />}
                      block
                    >
                      Add Inclusion
                    </Button>
                  </div>
                )}
              </Form.List>
            </Form.Item>
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Exclusions</h3>
            <Form.Item
              name="exclusions"
              className="mb-0!"
              rules={[
                {
                  required: false,
                  type: "array",
                },
              ]}
            >
              <Form.List name={"exclusions"}>
                {(fields, { add, remove }) => (
                  <div className="space-y-3">
                    {fields.map(({ key, name }) => (
                      <div key={key} className="flex items-start gap-2">
                        <Form.Item
                          name={[name]}
                          className="mb-0! flex-1"
                          rules={[{ required: true, message: "Enter activity exclusion" }]}
                        >
                          <Input placeholder="Entrance Fee, Visa & Insurance, Airfare, etc..." />
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          onClick={() => {
                            remove(name);
                          }}
                          icon={<MinusCircleIcon size={16} />}
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => {
                        add();
                      }}
                      icon={<PlusIcon size={16} />}
                      block
                    >
                      Add Exclusion
                    </Button>
                  </div>
                )}
              </Form.List>
            </Form.Item>
          </div>
        </div>
      </section>

      <Divider className="!my-2" />

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#131B2A]">Pricing and tariffs</h2>
        </div>
        <div className="space-y-6">
          <div className="space-y-3">
            <div hidden={category === "Transfers"}><ChildAgeBands form={form} /></div>
            <h3 className="text-sm font-medium text-gray-700">Regular tariff</h3>
            <Form.Item name={"tariff"} className="mb-0!" rules={[{ type: "array", required: true }]}>
              <Form.List name={"tariff"}>
                {(tariffs) => (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {tariffs.map((tariff) => (
                      <Card key={tariff.key} className="border border-gray-200 !shadow-none" styles={{ body: { padding: 16 } }}>
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-700">{user?.tenant?.currency[tariff.name]}</h4>
                          <ActivityTariffFields form={form} tariffName={tariff.name} category={category} />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Form.List>
            </Form.Item>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Seasonal tariff</h3>
            <Form.Item name={"seasonal"} className="mb-0!" rules={[{ type: "array" }]}>
              <Form.List name={"seasonal"}>
                {(seasonalTariffs, { add, remove }) => (
                  <div className="space-y-4">
                    {seasonalTariffs.map((seasonal) => (
                      <Card key={seasonal.key} className="border border-dashed border-gray-300 !shadow-none">
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="grid gap-4 md:grid-cols-2 flex-1">
                              <Form.Item
                                label="Date Range"
                                name={[seasonal.name, "daterange"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Select date range",
                                  },
                                  {
                                    // Custom validator to ensure the start date and end date is not overlapping with existing seasonal dates
                                    validator: (_, value) => {
                                      if (!value || !value[0] || !value[1]) {
                                        return Promise.resolve();
                                      }
                                      const startDate = value[0];
                                      const endDate = value[1];
                                      const existingDates = seasonalTariffs
                                        .filter((s) => s.name !== seasonal.name)
                                        .map((s) => form.getFieldValue(["seasonal", s.name, "daterange"]))
                                        .filter((dates) => (dates === null ? false : true));

                                      for (const dates of existingDates) {
                                        if (
                                          (startDate.isSameOrAfter(dates[0]) && startDate.isBefore(dates[1])) ||
                                          (endDate.isAfter(dates[0]) && endDate.isSameOrBefore(dates[1])) ||
                                          (startDate.isBefore(dates[0]) && endDate.isAfter(dates[1]))
                                        ) {
                                          return Promise.reject(
                                            new Error("Selected date range overlaps with existing seasonal dates"),
                                          );
                                        }
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                                className="mb-0!"
                              >
                                <RangePicker
                                  format={"DD MMMM"}
                                  minDate={dayjs(`01/01/${new Date().getFullYear()}`)}
                                  maxDate={dayjs(`12/31/${new Date().getFullYear()}`)}
                                  className="w-full"
                                />
                              </Form.Item>
                              <Form.Item
                                label="Reason"
                                name={[seasonal.name, "reason"]}
                                rules={[
                                  {
                                    max: 100,
                                    message: "Max 100 characters",
                                    type: "string",
                                  },
                                ]}
                                className="mb-0!"
                              >
                                <Input type="text" maxLength={100} placeholder="Peak season, Holiday rates..." />
                              </Form.Item>
                            </div>
                            <Button
                              type="text"
                              danger
                              onClick={() => {
                                remove(seasonal.name);
                              }}
                              icon={<MinusCircleIcon size={16} />}
                            />
                          </div>
                          <div>
                            <h4 className="mb-3 text-sm font-medium text-gray-700">Seasonal rates</h4>
                            <Form.List name={[seasonal.name, "tariff"]}>
                              {(tariffs) => (
                                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                  {tariffs.map((tariff) => (
                                    <Card key={tariff.key} className="border border-gray-200 !shadow-none" styles={{ body: { padding: 16 } }}>
                                      <div className="space-y-3">
                                        <h5 className="text-xs font-medium text-gray-600">
                                          {user?.tenant?.currency[tariff.name]}
                                        </h5>
                                        <ActivityTariffFields form={form} tariffName={tariff.name} category={category} />
                                      </div>
                                    </Card>
                                  ))}
                                </div>
                              )}
                            </Form.List>
                          </div>
                        </div>
                      </Card>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => {
                        add({
                          daterange: null,
                          tariff: user?.tenant?.currency.map((currency) => ({
                            costForAdult: null,
                            costForCWB: null,
                            costForInfant: null,
                            currency: currency,
                          })),
                        });
                      }}
                      icon={<PlusIcon size={16} />}
                      block
                    >
                      Add Date Range
                    </Button>
                  </div>
                )}
              </Form.List>
            </Form.Item>
          </div>
        </div>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          onClick={() => {
            if (isModal && onCancel) {
              onCancel();
            } else {
              navigate(buildOrgRoute("activities-repository"));
            }
          }}
        >
          Cancel
        </Button>
        <Button type="primary" htmlType="submit">
          Save Changes
        </Button>
      </div>
      <VehicleRatesModal
        form={form}
        isOpen={isVehicleRatesModalOpen}
        onClose={closeVehicleRatesModal}
        currentVehicleIndex={currentVehicleIndex}
        currencies={user?.tenant?.currency || []}
      />
    </Form>
  );

  if (isModal) {
    return <div className="max-h-[80vh] overflow-y-auto p-4">{formContent}</div>;
  }

  return (
    <div className="space-y-4">
      <OrgPageHeader
        title={pageTitle}
        description={pageSubtitle}
        actions={<Button onClick={() => navigate(buildOrgRoute("activities-repository"))}>Back to activities</Button>}
      />

      <OrgTableCard className="p-4 sm:p-6">{formContent}</OrgTableCard>
    </div>
  );
};

export default EditActivityRepositoryPage;
